import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentUser, signOut, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Box, Button, Typography, CircularProgress, Container } from '@mui/material';
import { configureAmplify } from '../auth/auth-config';

const BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

const AuthWrapper = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const isInitialCheckRef = useRef(true);
  const isProcessingCallbackRef = useRef(false);

  const handleSignOut = async () => {
    console.log('AuthWrapper handleSignOut');
    try {
      await signOut();
      setUser(null);
      sessionStorage.removeItem('auth_redirect_attempted');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignIn = useCallback(async () => {
    console.log('AuthWrapper handleSignIn');
    try {
      await signInWithRedirect({ provider: { custom: 'AzureAD' } });
    } catch (error) {
      console.error('Error signing in:', error);
      setAuthError(error.message || 'Sign in failed');
      setLoading(false);
    }
  }, []);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log('AuthWrapper: User authenticated:', currentUser.username);
      setUser(currentUser);
      sessionStorage.removeItem('auth_redirect_attempted');
      setLoading(false);
      if (window.location.search.includes('code=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      return true;
    } catch (err) {
      console.log('AuthWrapper: User is not authenticated:', err.message || err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (BYPASS_AUTH) return;
    console.log('AuthWrapper: Configuring Amplify');
    configureAmplify();

    const hubListenerCancel = Hub.listen('auth', async ({ payload }) => {
      console.log('AuthWrapper Hub event:', payload.event, payload);

      switch (payload.event) {
        case 'signInWithRedirect':
          isProcessingCallbackRef.current = true;
          break;
        case 'signInWithRedirect_failure':
          console.error('AuthWrapper: OAuth redirect failed:', payload.data);
          setAuthError(payload.data?.message || 'Authentication failed');
          sessionStorage.removeItem('auth_redirect_attempted');
          isProcessingCallbackRef.current = false;
          setLoading(false);
          break;
        case 'signedIn':
          console.log('AuthWrapper: User signed in via Hub');
          await checkUser();
          isProcessingCallbackRef.current = false;
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'tokenRefresh':
          await checkUser();
          break;
        case 'tokenRefresh_failure':
          setUser(null);
          setLoading(false);
          break;
        default:
          break;
      }
    });

    return () => hubListenerCancel();
  }, [checkUser]);

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('AuthWrapper: Starting authentication initialization');

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const hasError = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      if (hasError) {
        console.error('AuthWrapper: OAuth error:', hasError, errorDescription);
        setAuthError(errorDescription || hasError);
        sessionStorage.removeItem('auth_redirect_attempted');
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }

      if (code) {
        console.log('AuthWrapper: OAuth callback detected, waiting for Amplify...');
        isProcessingCallbackRef.current = true;
        setTimeout(async () => {
          const isAuthenticated = await checkUser();
          if (!isAuthenticated && !isProcessingCallbackRef.current) {
            setLoading(false);
          }
        }, 2000);
        return;
      }

      console.log('AuthWrapper: Checking existing authentication status');
      const isAuthenticated = await checkUser();

      if (!isAuthenticated) {
        const redirectAttempted = sessionStorage.getItem('auth_redirect_attempted');

        if (isInitialCheckRef.current && !redirectAttempted) {
          console.log('AuthWrapper: No user found, redirecting to SSO...');
          sessionStorage.setItem('auth_redirect_attempted', 'true');
          isInitialCheckRef.current = false;
          handleSignIn();
          return;
        } else {
          setLoading(false);
        }
      }

      isInitialCheckRef.current = false;
    };

    if (BYPASS_AUTH) return;
    const timer = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timer);
  }, [checkUser, handleSignIn]);

  if (BYPASS_AUTH) return <>{children}</>;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary">Authenticating...</Typography>
        </Box>
      </Container>
    );
  }

  if (authError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
          <Typography variant="h6" color="error" gutterBottom>Authentication Error</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{authError}</Typography>
          <Button variant="contained" size="large" onClick={() => {
            setAuthError(null);
            sessionStorage.removeItem('auth_redirect_attempted');
            handleSignIn();
          }}>Try Again</Button>
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
          <Typography variant="h6" color="text.secondary" gutterBottom>Authentication Required</Typography>
          <Button variant="contained" size="large" onClick={handleSignIn} sx={{ mt: 2 }}>Sign In</Button>
        </Box>
      </Container>
    );
  }

  return <>{children}</>;
};

export default AuthWrapper;
